import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
// KTK-283 — Verbose process trace modal for the three manual journalist
// triggers (Field Reporter / Daily digest / Synthesis).
//
// The trigger endpoints return a `process_log: ProcessLogEntry[]` along
// with structured per-pass detail (channel scans, vault writes, cluster
// decisions, linker actions). This modal renders the trace as a
// chronological step list with level-based tints, and surfaces the
// pass-specific summary above it.
//
// Akari's directive (KTK-282 follow-up): show the *process*, not the
// *output*. Output content already lives in the Vault tab. The modal is
// for "what did the pass actually do, in order, with what data".
import { useEffect, useState } from "react";
export function JournalistTraceModal({ pass, onClose }) {
    // Esc closes — keyboard parity with the rest of the workspace
    // settings dialogs.
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape")
                onClose();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);
    const log = pickProcessLog(pass);
    const status = pickStatus(pass);
    const watermarkAdvanced = pickWatermarkAdvanced(pass);
    const triggeredAt = pickTriggeredAt(pass);
    return (_jsx("div", { style: {
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "grid",
            placeItems: "center",
            zIndex: 200,
        }, children: _jsxs("div", { style: {
                background: "var(--paper)",
                border: "1px solid var(--hairline)",
                borderRadius: "var(--radius-lg)",
                width: "min(960px, 94vw)",
                maxHeight: "88vh",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                boxShadow: "0 12px 48px rgba(0,0,0,0.25)",
            }, children: [_jsxs("div", { style: {
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "12px 18px",
                        borderBottom: "1px solid var(--hairline)",
                    }, children: [_jsxs("div", { style: { flex: 1, minWidth: 0 }, children: [_jsxs("div", { style: {
                                        fontSize: 14,
                                        fontWeight: 600,
                                        color: "var(--ink)",
                                    }, children: [pass.label, " \u00B7 process trace"] }), _jsxs("div", { className: "ws-mono", style: {
                                        fontSize: 11,
                                        color: "var(--ink-3)",
                                        marginTop: 2,
                                    }, children: [triggeredAt ? new Date(triggeredAt).toLocaleString() : "—", status && (_jsxs(_Fragment, { children: [" · ", _jsx(StatusPill, { status: status })] })), watermarkAdvanced !== undefined && (_jsxs(_Fragment, { children: [" · watermark ", watermarkAdvanced ? "advanced" : "held"] }))] })] }), _jsx("button", { type: "button", className: "ws-icon-btn", onClick: onClose, "aria-label": "Close", title: "Close (Esc)", children: "\u00D7" })] }), _jsxs("div", { style: {
                        flex: 1,
                        minHeight: 0,
                        overflowY: "auto",
                        padding: "16px 22px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 14,
                    }, children: [_jsx(PassSummary, { pass: pass }), _jsx(PromptBlock, { log: log }), _jsx(TraceList, { log: log })] })] }) }));
}
function StatusPill({ status }) {
    const tint = status === "error"
        ? "color-mix(in oklab, oklch(58% 0.18 25) 18%, var(--paper))"
        : status === "no_new_messages" || status === "no_op"
            ? "var(--paper-3)"
            : "color-mix(in oklab, oklch(70% 0.14 145) 18%, var(--paper))";
    const fg = status === "error"
        ? "oklch(40% 0.16 25)"
        : status === "no_new_messages" || status === "no_op"
            ? "var(--ink-2)"
            : "oklch(35% 0.13 145)";
    return (_jsx("span", { style: {
            display: "inline-block",
            padding: "1px 8px",
            background: tint,
            color: fg,
            borderRadius: 999,
            fontSize: 10.5,
            fontFamily: "var(--mono)",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
        }, children: status.replace(/_/g, " ") }));
}
// Per-pass structured summary section. Sits above the chronological
// trace and surfaces the high-signal lists (channel scans, vault writes,
// cluster decisions, etc) without making the operator scan the whole
// process_log to reconstruct them.
function PassSummary({ pass }) {
    if (pass.kind === "extraction") {
        const r = pass.result;
        return (_jsxs("div", { style: summaryWrap, children: [_jsx(SummaryRow, { label: "Watermark", value: r.previous_last_run && r.last_run ? (_jsxs("span", { className: "ws-mono", children: [short(r.previous_last_run), " \u2192 ", short(r.last_run)] })) : r.last_run ? (_jsx("span", { className: "ws-mono", children: short(r.last_run) })) : ("—") }), _jsx(SummaryRow, { label: "Messages scanned", value: _jsxs(_Fragment, { children: [_jsx("span", { className: "ws-mono", children: r.messages_processed }), r.channels_processed !== undefined && (_jsxs(_Fragment, { children: [" ", "across ", _jsx("span", { className: "ws-mono", children: r.channels_processed }), " ", "channels"] }))] }) }), r.vault_writes?.writes && r.vault_writes.writes.length > 0 && (_jsx(KvBlock, { label: "Vault writes", children: r.vault_writes.writes.map((w, i) => (_jsxs("div", { style: kvRow, children: [_jsxs("span", { className: "ws-mono", style: { color: "var(--ink-2)" }, children: [w.layer, "/", w.slug] }), _jsx("span", { className: "ws-mono", children: w.action })] }, `${w.layer}/${w.slug}-${i}`))) })), r.cross_channel_ref_detail && r.cross_channel_ref_detail.length > 0 && (_jsx(KvBlock, { label: "Cross-channel refs", children: r.cross_channel_ref_detail.map((x, i) => (_jsxs("div", { style: { ...kvRow, alignItems: "baseline" }, children: [_jsx("span", { className: "ws-mono", style: { color: "var(--ink-2)" }, children: x.slug }), _jsxs("span", { style: { fontSize: 12, color: "var(--ink)" }, children: [x.topic, " ", _jsxs("span", { className: "ws-mono", style: { color: "var(--ink-3)" }, children: ["(", x.channels.join(", "), ")"] })] })] }, `${x.slug}-${i}`))) }))] }));
    }
    if (pass.kind === "digest") {
        const r = pass.result;
        return (_jsxs("div", { style: summaryWrap, children: [r.digest_slug && (_jsx(SummaryRow, { label: "Digest slug", value: _jsx("span", { className: "ws-mono", children: r.digest_slug }) })), r.source_raw_count !== undefined && (_jsx(SummaryRow, { label: "Raw entries folded", value: _jsx("span", { className: "ws-mono", children: r.source_raw_count }) })), r.deleted_raw !== undefined && (_jsx(SummaryRow, { label: "Raw entries deleted", value: _jsx("span", { className: "ws-mono", children: r.deleted_raw }) }))] }));
    }
    // synthesis
    const r = pass.result;
    const promoted = (r.decisions ?? []).filter((d) => d.action === "promoted");
    const skipped = (r.decisions ?? []).filter((d) => d.action !== "promoted");
    return (_jsxs("div", { style: summaryWrap, children: [_jsx(SummaryRow, { label: "Promotions", value: _jsxs(_Fragment, { children: [_jsx("span", { className: "ws-mono", children: r.semantic_written ?? 0 }), " semantic \u00B7", " ", _jsx("span", { className: "ws-mono", children: r.procedural_written ?? 0 }), " procedural \u00B7", " ", _jsx("span", { className: "ws-mono", children: r.identity_written ?? 0 }), " identity"] }) }), _jsx(SummaryRow, { label: "Linker actions", value: _jsxs(_Fragment, { children: [_jsx("span", { className: "ws-mono", children: r.linker_actions_applied ?? 0 }), " applied"] }) }), promoted.length > 0 && (_jsx(KvBlock, { label: "Promoted", children: promoted.map((d, i) => (_jsx(DecisionRow, { d: d }, `${d.layer}/${d.slug}-${i}`))) })), skipped.length > 0 && (_jsx(KvBlock, { label: "Skipped", children: skipped.map((d, i) => (_jsx(DecisionRow, { d: d }, `${d.layer}/${d.slug}-${i}`))) })), r.linker_results && r.linker_results.length > 0 && (_jsx(KvBlock, { label: "Linker results", children: r.linker_results.map((lr, i) => (_jsxs("div", { style: kvRow, children: [_jsx("span", { className: "ws-mono", style: { color: "var(--ink-2)" }, children: lr.target }), _jsxs("span", { className: "ws-mono", children: [lr.applied ? "applied" : "skipped", lr.reason ? ` · ${lr.reason}` : ""] })] }, `${lr.target}-${i}`))) }))] }));
}
function DecisionRow({ d }) {
    return (_jsxs("div", { style: { ...kvRow, alignItems: "baseline" }, children: [_jsxs("span", { className: "ws-mono", style: { color: "var(--ink-2)" }, children: [d.layer, "/", d.slug] }), _jsxs("span", { style: { fontSize: 12, color: "var(--ink)" }, children: [d.action, d.skip_reason ? ` · ${d.skip_reason}` : "", d.source_episodic_slugs && d.source_episodic_slugs.length > 0 && (_jsxs("span", { className: "ws-mono", style: { color: "var(--ink-3)" }, children: [" ", "\u2190 ", d.source_episodic_slugs.join(", ")] }))] })] }));
}
function SummaryRow({ label, value, }) {
    return (_jsxs("div", { style: kvRow, children: [_jsx("span", { style: kvLabel, children: label }), _jsx("span", { style: { fontSize: 12.5, color: "var(--ink)" }, children: value })] }));
}
function KvBlock({ label, children, }) {
    return (_jsxs("div", { style: {
            border: "1px solid var(--hairline-2)",
            borderRadius: "var(--radius-sm)",
            background: "var(--paper-2)",
            overflow: "hidden",
        }, children: [_jsx("div", { style: {
                    padding: "6px 10px",
                    fontFamily: "var(--mono)",
                    fontSize: 9.5,
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    color: "var(--ink-3)",
                    borderBottom: "1px solid var(--hairline-2)",
                }, children: label }), _jsx("div", { style: { padding: "6px 10px", display: "flex", flexDirection: "column", gap: 3 }, children: children })] }));
}
// Renders the literal system + user prompt strings sent to the model
// for this pass. Sourced from the `llm.prompt` log step's `data` payload
// (KTK-287) — these are the exact strings on the wire, not a
// re-composition. Each section is collapsible and gets a copy button.
function PromptBlock({ log }) {
    const entry = log?.find((e) => e.step === "llm.prompt");
    if (!entry?.data)
        return null;
    const data = entry.data;
    const sys = typeof data.system_prompt === "string" ? data.system_prompt : null;
    const user = typeof data.user_prompt === "string" ? data.user_prompt : null;
    if (!sys && !user)
        return null;
    return (_jsxs("div", { style: {
            border: "1px solid var(--hairline)",
            borderRadius: "var(--radius-sm)",
            background: "var(--paper-2)",
            overflow: "hidden",
        }, children: [_jsx("div", { style: {
                    padding: "6px 12px",
                    fontFamily: "var(--mono)",
                    fontSize: 9.5,
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    color: "var(--ink-3)",
                    borderBottom: "1px solid var(--hairline-2)",
                    background: "var(--paper-3)",
                }, children: "Prompt sent to model" }), _jsxs("div", { style: { display: "flex", flexDirection: "column" }, children: [sys && _jsx(PromptSection, { label: "system", text: sys }), user && _jsx(PromptSection, { label: "user", text: user })] })] }));
}
function PromptSection({ label, text }) {
    const [expanded, setExpanded] = useState(false);
    const lines = text.split("\n");
    const preview = lines.slice(0, 4).join("\n");
    const hasMore = lines.length > 4;
    const onCopy = (e) => {
        e.stopPropagation();
        void navigator.clipboard.writeText(text).catch(() => { });
    };
    return (_jsxs("div", { style: {
            borderTop: "1px solid var(--hairline-2)",
            padding: "8px 12px",
        }, children: [_jsxs("div", { style: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 6,
                }, children: [_jsxs("span", { className: "ws-mono", style: {
                            fontSize: 10,
                            textTransform: "uppercase",
                            letterSpacing: "0.08em",
                            color: "var(--ink-3)",
                        }, children: [label, " \u00B7 ", text.length.toLocaleString(), " chars"] }), _jsxs("div", { style: { display: "flex", gap: 6 }, children: [hasMore && (_jsx("button", { type: "button", onClick: () => setExpanded((v) => !v), style: pillBtn, children: expanded ? "collapse" : "expand" })), _jsx("button", { type: "button", onClick: onCopy, style: pillBtn, children: "copy" })] })] }), _jsx("pre", { style: {
                    margin: 0,
                    padding: "10px 12px",
                    fontFamily: "'Cascadia Code', 'Fira Code', 'JetBrains Mono', monospace",
                    fontSize: 11.5,
                    lineHeight: 1.55,
                    color: "var(--ink)",
                    background: "var(--paper)",
                    border: "1px solid var(--hairline-2)",
                    borderRadius: "var(--radius-sm)",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    maxHeight: expanded ? 360 : 120,
                    overflow: expanded ? "auto" : "hidden",
                }, children: expanded ? text : preview + (hasMore ? "\n…" : "") })] }));
}
const pillBtn = {
    font: "inherit",
    fontSize: 10.5,
    padding: "1px 8px",
    borderRadius: 999,
    border: "1px solid var(--hairline)",
    background: "var(--paper)",
    color: "var(--ink-3)",
    cursor: "pointer",
};
// Chronological process_log list. Each entry is one step; `data` (when
// present) renders as a collapsed JSON block beneath the line — click to
// expand. Levels tint the left rule.
function TraceList({ log }) {
    if (!log || log.length === 0) {
        return (_jsx("div", { style: {
                padding: "10px 12px",
                color: "var(--ink-3)",
                fontFamily: "var(--serif)",
                fontStyle: "italic",
                fontSize: 12,
                background: "var(--paper-2)",
                border: "1px dashed var(--hairline)",
                borderRadius: "var(--radius-sm)",
            }, children: "No trace was recorded for this pass." }));
    }
    return (_jsxs("div", { style: {
            border: "1px solid var(--hairline)",
            borderRadius: "var(--radius-sm)",
            background: "var(--paper)",
            overflow: "hidden",
        }, children: [_jsxs("div", { style: {
                    padding: "6px 12px",
                    fontFamily: "var(--mono)",
                    fontSize: 9.5,
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    color: "var(--ink-3)",
                    borderBottom: "1px solid var(--hairline)",
                    background: "var(--paper-2)",
                }, children: ["Process log \u00B7 ", log.length, " step", log.length === 1 ? "" : "s"] }), _jsx("div", { children: log.map((entry, i) => (_jsx(TraceRow, { entry: entry, startTs: log[0]?.ts }, `${entry.ts}-${entry.step}-${i}`))) })] }));
}
function TraceRow({ entry, startTs, }) {
    const [expanded, setExpanded] = useState(false);
    const hasData = !!entry.data &&
        typeof entry.data === "object" &&
        Object.keys(entry.data).length > 0;
    const elapsedMs = startTs ? deltaMs(startTs, entry.ts) : null;
    const tint = entry.level === "error"
        ? "oklch(58% 0.18 25)"
        : entry.level === "warn"
            ? "oklch(72% 0.14 70)"
            : "var(--hairline-2)";
    return (_jsxs("div", { style: {
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            gap: 10,
            padding: "8px 12px",
            borderBottom: "1px solid var(--hairline-2)",
            background: entry.level === "error"
                ? "color-mix(in oklab, oklch(58% 0.18 25) 6%, var(--paper))"
                : entry.level === "warn"
                    ? "color-mix(in oklab, oklch(72% 0.14 70) 6%, var(--paper))"
                    : "var(--paper)",
            borderLeft: `3px solid ${tint}`,
        }, children: [_jsx("div", { className: "ws-mono", style: {
                    fontSize: 10.5,
                    color: "var(--ink-3)",
                    minWidth: 70,
                    paddingTop: 1,
                }, title: entry.ts, children: elapsedMs !== null ? `+${elapsedMs}ms` : new Date(entry.ts).toLocaleTimeString() }), _jsxs("div", { style: { minWidth: 0 }, children: [_jsxs("div", { style: {
                            display: "flex",
                            alignItems: "baseline",
                            gap: 8,
                            flexWrap: "wrap",
                        }, children: [_jsx("span", { className: "ws-mono", style: {
                                    fontSize: 11.5,
                                    fontWeight: 600,
                                    color: entry.level === "error" ? "oklch(40% 0.16 25)" : "var(--ink)",
                                }, children: entry.step }), _jsx("span", { style: { fontSize: 12.5, color: "var(--ink-2)" }, children: entry.detail }), hasData && (_jsx("button", { type: "button", onClick: () => setExpanded((v) => !v), style: {
                                    marginLeft: "auto",
                                    font: "inherit",
                                    fontSize: 10.5,
                                    padding: "1px 8px",
                                    borderRadius: 999,
                                    border: "1px solid var(--hairline)",
                                    background: "var(--paper-2)",
                                    color: "var(--ink-3)",
                                    cursor: "pointer",
                                }, children: expanded ? "hide" : "data" }))] }), hasData && expanded && (_jsx("pre", { style: {
                            margin: "6px 0 0",
                            padding: "8px 10px",
                            fontFamily: "'Cascadia Code', 'Fira Code', 'JetBrains Mono', monospace",
                            fontSize: 11,
                            lineHeight: 1.55,
                            color: "var(--ink-2)",
                            background: "var(--paper-2)",
                            border: "1px solid var(--hairline-2)",
                            borderRadius: "var(--radius-sm)",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                        }, children: JSON.stringify(entry.data, null, 2) }))] })] }));
}
function pickProcessLog(p) {
    return p.result.process_log;
}
function pickStatus(p) {
    return p.result.status;
}
function pickWatermarkAdvanced(p) {
    return p.result.watermark_advanced;
}
function pickTriggeredAt(p) {
    return p.result.triggered_at;
}
function deltaMs(start, t) {
    const s = Date.parse(start);
    const e = Date.parse(t);
    if (!Number.isFinite(s) || !Number.isFinite(e))
        return null;
    return Math.max(0, e - s);
}
function short(iso) {
    // Format an ISO timestamp as HH:MM:SS in the operator's locale. The
    // header already shows the fully-qualified trigger time; the per-row
    // and watermark lines just need wall-clock precision.
    try {
        const d = new Date(iso);
        return d.toLocaleTimeString();
    }
    catch {
        return iso;
    }
}
const summaryWrap = {
    display: "flex",
    flexDirection: "column",
    gap: 8,
};
const kvRow = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    fontSize: 12,
    minHeight: 18,
};
const kvLabel = {
    fontFamily: "var(--mono)",
    fontSize: 9.5,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: "var(--ink-3)",
};
//# sourceMappingURL=JournalistTraceModal.js.map